import { Component, HostListener, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { CashRegisterService, tryResolveAlpha3CountryCode } from '@bcmServices/cash-registers/cash-register.service';
import { forkJoin, Observable, of, Subject, switchMap } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { InvoicePosition } from '@shared/models/invoice-position';
import { Person } from '@shared/models/person';
import { InvoiceAddressStructured } from '@shared/models/invoice';
import { Company } from '@shared/models/company';
import { BcmCashRegister } from '@shared/models/bcm-cash-register';
import { BcmReceipt } from '@shared/models/bcm-receipt';
import { BcmFinancialRecord } from '@shared/models/bcm-financial-record';
import { ReceiptTypes } from '@shared/invoice-types';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { BcmService } from '@modules/bcm/bcm.service';
import {
    MAT_DIALOG_DATA,
    MatDialogRef
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TsePaymentType } from '@shared/models/tse/TsePaymentType';
import { BcmInvoiceWdTemplate } from '@shared/models/bcm-invoice-wd-template';
import { FinancialRecordPrintService } from '@modules/bcm/@shared/services/financial-record-print.service';
import { enrichReceiptForCancellationPrint, resolveReceiptPrintDate } from '@modules/bcm/@shared/services/financial-record-receipt-print.util';
import { PersonApiService, CompanyApiService } from '@modules/bcm/@shared/services';
import { NetworkPrintersService } from '@modules/bcm/settings/network-printers/network-printers.service';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import { BcmUserSettingsStateService } from '@bcmServices/settings/bcm-user-settings-state.service';
import { BcmUserLastUsedReceiptPrinterSettings, BcmUserSettingKey } from '@shared/models/bcm-settings-user';
import { BcmNetworkPrinter } from '@shared/models/network-printer';
import { AppNotificationService } from '@core/services/app-notification.service';
import { UserService } from '@core/services/user.service';
import { resolveSelectedReceiptPrinter } from '@modules/bcm/accounting/invoices/invoice-form/invoice-form-receipt-printer.util';
import { roundCurrency } from '@modules/bcm/@shared/pipes/dynamic-price-rounded.pipe';
import { TranslationService } from '@core/translation/translation.service';
import { buildStructuredAddressString } from '@shared/functions/sanitize-address-field';
import {
    hasReceiptCreationError,
    isCancellationMode,
    isTseDocumentCreatedError,
    mapReceiptPaymentTypeToTse,
    resolveCheckoutCloseResult,
    resolveCheckoutDialogClosePayload,
    resolveCheckoutTotalSum,
    shouldPrintAfterCheckout,
} from './pay-by-cash-dialog.helper';
import {
    buildInvalidQuantityCheckoutError,
    findPositionsWithInvalidQuantity,
} from '@modules/bcm/accounting/invoices/invoice-form/invoice-form-position-checkout-validation.util';

@Component({
    selector: 'app-pay-by-cash-dialog',
    templateUrl: './pay-by-cash-dialog.component.html',
    styleUrls: ['./pay-by-cash-dialog.component.scss'],
    standalone: false
})
export class PayByCashDialogComponent implements OnInit, OnDestroy {
    private static readonly KLEINBETRAGSRECHNUNG_LIMIT = 250;

    _unsubscribeAll = new Subject();

    parentFormGroup: UntypedFormGroup;

    positions: InvoicePosition[];
    person: Person;
    company: Company;
    receiptToCancel: BcmReceipt;
    addPositionsBackToCoaster: boolean;
    cancellationReason: string;

    isProcessing = false;
    isPrinting = false;
    printFailed = false;
    updatedPositions: InvoicePosition[] = [];

    checkAddonDone = false;
    addonStatus = false;

    checkCashRegisterDone = false;
    cashRegister: BcmCashRegister;

    requestReceiptDone = false;
    receipt: BcmReceipt;
    receiptErrorMessages: string[];
    /** Beleg liegt schon bei der TSE, wurde aber nicht gespeichert – kein erneuter Versuch möglich. */
    receiptCreationLocked = false;
    receiptRequestFailed = false;

    canStartPaymentProcess = true;

    receiptPrinters: BcmNetworkPrinter[] = [];
    selectedReceiptPrinter: BcmNetworkPrinter | null = null;
    receiptPrintersLoaded = false;
    printCancellationReceipt = true;

    get shouldPrint(): boolean {
        return shouldPrintAfterCheckout({
            isCancellationMode: this.isCancellationMode,
            dataShouldPrint: this.data?.shouldPrint,
            printCancellationReceipt: this.printCancellationReceipt,
            paymentType: this.paymentType,
        });
    }

    paymentType: TsePaymentType = TsePaymentType.CASH;

    readonly TsePaymentType = TsePaymentType;

    wordTemplate: BcmInvoiceWdTemplate;

    address: string;
    addressStructured: InvoiceAddressStructured | null;
    note: string;

    recipientErrors: string[] = [];

    totalSum: number;

    /** Vom Kunden gegebener Betrag (Numpad-Eingabe) */
    givenAmount = 0;

    /** Rückgeld = givenAmount - totalSum (nur wenn positiv) */
    get changeAmount(): number {
        const change = this.givenAmount - Math.abs(this.totalSum);
        return change > 0 ? change : 0;
    }

    /** Nur nach einer gescheiterten Belegerstellung – nicht bei lokalen Prüfungen wie „Betrag zu niedrig“. */
    get showReceiptCreationErrorHint(): boolean {
        return this.receiptRequestFailed && hasReceiptCreationError(this.receiptErrorMessages, this.receipt);
    }

    get isCancellationMode(): boolean {
        return isCancellationMode(this.receiptToCancel);
    }

    get cancellationPaymentTypeLabelKey(): string {
        switch (this.paymentType) {
            case TsePaymentType.EC_CARD:
                return 'accounting.checkout.cardType.ec';
            case TsePaymentType.CREDIT_CARD:
                return 'accounting.checkout.cardType.credit';
            default:
                return 'accounting.checkout.tab.cash';
        }
    }

    /** Interne Eingabe-Puffer für das Numpad (als String) */
    private _numpadBuffer = '0';

    constructor(public dialogRef: MatDialogRef<PayByCashDialogComponent>,
                @Optional() @Inject(MAT_DIALOG_DATA) private data: {
                    positions: InvoicePosition[],
                    person: Person,
                    company: Company,
                    formGroup: FormGroup,
                    receiptToCancel: BcmReceipt
                    addPositionsBackToCoaster: boolean,
                    wordTemplate: BcmInvoiceWdTemplate,
                    addressStructured?: InvoiceAddressStructured,
                    shouldPrint?: boolean,
                    receiptPrinter?: BcmNetworkPrinter | null,
                    cancellationReason?: string,
                    totalAmount?: number,
                },
                private _bcmTenantService: BcmTenantService,
                private _cashRegisterService: CashRegisterService,
                private _bcmService: BcmService,
                private _router: Router,
                private _printService: FinancialRecordPrintService,
                private _personApiService: PersonApiService,
                private _companyApiService: CompanyApiService,
                private _appNotificationService: AppNotificationService,
                private _networkPrintersService: NetworkPrintersService,
                private _userService: UserService,
                private _bcmUserSettingsState: BcmUserSettingsStateService,
                private _bcmUserSettingsFacade: BcmUserSettingsFacade) {
        if (!data) {
            this.canStartPaymentProcess = false;
            this.receiptErrorMessages = [TranslationService.translate('accounting.checkout.error.initFailed')];
            return;
        }
        this.positions = data?.receiptToCancel?.positions ?? data?.positions ?? [];
        this.totalSum = resolveCheckoutTotalSum({
            receiptToCancel: data?.receiptToCancel,
            positions: this.positions,
            totalAmount: data?.totalAmount,
            getTotal: (positions, sign) => _cashRegisterService.getTotal(positions, sign),
        });
        this.person = data?.person;
        this.company = data?.company;
        this.parentFormGroup = data?.formGroup;
        this.receiptToCancel = data?.receiptToCancel;
        this.addPositionsBackToCoaster = data?.addPositionsBackToCoaster;
        this.wordTemplate = data?.wordTemplate;
        this.addressStructured = data?.addressStructured ?? null;
        this.cancellationReason = data?.cancellationReason;

        if (this.receiptToCancel?.id) {
            this.paymentType = mapReceiptPaymentTypeToTse(this.receiptToCancel.paymentType) ?? TsePaymentType.CASH;
        }

        this.checkTseConditions();
    }

    ngOnInit(): void {
        if (this.isCancellationMode && this.paymentType === TsePaymentType.CASH) {
            this._loadReceiptPrinters();
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    checkTseConditions(): void {
        of(this._bcmTenantService.checkPermission(BcmTenantPermission.CASH))
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap((hasPermission) => {
                    this.checkAddonDone = true;
                    this.addonStatus = hasPermission;
                    if (!hasPermission) {
                        this.canStartPaymentProcess = false;
                        return of(null);
                    }
                    return this._cashRegisterService.getLoggedInCashRegisterForUser();
                }),
                switchMap((cashRegister) => {
                    if (!this.addonStatus) {
                        return of(null);
                    }
                    this.checkCashRegisterDone = true;
                    this.cashRegister = cashRegister;
                    if (!cashRegister?.id) {
                        return of(cashRegister);
                    }
                    this.note = this.parentFormGroup?.get('note')?.value;
                    this.address = this.receiptToCancel?.address ?? this._computeAddressString();
                    // Person/Company aus der Auswahlliste haben oft keine country-Relation,
                    // obwohl in den Stammdaten ein Land hinterlegt ist. Vor der Validierung
                    // bei Betrag > 250 € per API nachladen.
                    if (Math.abs(this.totalSum) > PayByCashDialogComponent.KLEINBETRAGSRECHNUNG_LIMIT && (this.person?.id || this.company?.id)) {
                        const needsPerson = this.person?.id && !this.person?.country && !this.person?.countryCode;
                        const needsCompany = this.company?.id && !this.company?.country && !this.company?.countryCode;
                        const fetches: Observable<unknown>[] = [];
                        if (needsPerson) {
                            fetches.push(this._personApiService.getOne(this.person.id).pipe(tap((p: Person) => (this.person = p))));
                        }
                        if (needsCompany) {
                            fetches.push(this._companyApiService.getOne(this.company.id).pipe(tap((c: Company) => (this.company = c))));
                        }
                        if (fetches.length > 0) {
                            return forkJoin(fetches).pipe(map(() => cashRegister));
                        }
                    }
                    return of(cashRegister);
                })
            )
            .subscribe(
                (cashRegister) => {
                    this.recipientErrors = [];
                    if (!this.addonStatus) {
                        return;
                    }
                    if (cashRegister?.id) {
                        this.canStartPaymentProcess = true;
                        if (Math.abs(this.totalSum) > PayByCashDialogComponent.KLEINBETRAGSRECHNUNG_LIMIT) {
                            const recipientErrors: string[] = [];
                            const hasLinkedMasterData = !!(this.person?.id || this.company?.id);
                            const hasManualStructuredAddress = !!(
                                this.addressStructured?.lastName ||
                                this.addressStructured?.firstName ||
                                (this.addressStructured?.street && this.addressStructured?.city)
                            );

                            if (!hasLinkedMasterData && !hasManualStructuredAddress) {
                                recipientErrors.push(
                                    TranslationService.translate('accounting.checkout.error.recipientInvalid')
                                );
                            } else {
                                // Formularwerte (addressStructured) haben Vorrang vor Stammdaten –
                                // der Nutzer kann fehlende Felder direkt im Rechnungsformular ergänzen.
                                const street = this.addressStructured?.street
                                    || this.person?.street || this.company?.street;
                                const zip = this.addressStructured?.zip
                                    || this.person?.postCode || this.company?.postCode;
                                const city = this.addressStructured?.city
                                    || this.person?.city || this.company?.city;
                                const countryInput = this.addressStructured?.country
                                    || this.person?.country?.de
                                    || this.company?.country?.de
                                    || this.person?.countryCode
                                    || this.company?.countryCode;
                                const country = tryResolveAlpha3CountryCode(countryInput);

                                if (!street) {
                                    recipientErrors.push(
                                        TranslationService.translate('accounting.checkout.error.recipientMissingStreet')
                                    );
                                }
                                if (!zip) {
                                    recipientErrors.push(
                                        TranslationService.translate('accounting.checkout.error.recipientMissingZip')
                                    );
                                }
                                if (!city) {
                                    recipientErrors.push(
                                        TranslationService.translate('accounting.checkout.error.recipientMissingCity')
                                    );
                                }
                                if (!country) {
                                    recipientErrors.push(
                                        TranslationService.translate('accounting.checkout.error.recipientMissingCountry')
                                    );
                                }
                                if (recipientErrors.length > 0 && hasLinkedMasterData) {
                                    recipientErrors.push(
                                        TranslationService.translate('accounting.checkout.error.recipientFixHint')
                                    );
                                }
                            }
                            if (recipientErrors.length > 0) {
                                this.recipientErrors = recipientErrors;
                            }
                        }
                        if (this.recipientErrors.length === 0 && this.receiptToCancel?.id && !cashRegister.currentUser?.canCancel) {
                            this.recipientErrors = [
                                TranslationService.translate('accounting.checkout.error.cancelNotPermitted')
                            ];
                        }
                    } else {
                        if (this.receiptToCancel?.id) {
                            this.recipientErrors = [
                                TranslationService.translate('accounting.checkout.error.cancelNoCashRegister')
                            ];
                        } else {
                            this.recipientErrors = [
                                TranslationService.translate('accounting.checkout.error.payNoCashRegister')
                            ];
                        }
                    }
                },
                (err) => {
                    this.canStartPaymentProcess = false;
                    this.recipientErrors = this._toErrorMessages(err);
                }
            );
    }

    startPaymentProcess(): void {
        if (this.isProcessing || this.isPrinting || this.receiptCreationLocked) {
            return;
        }
        this.receiptRequestFailed = false;
        if (!this.receiptToCancel?.id && (!this.positions || this.positions.length === 0)) {
            this.receiptErrorMessages = [
                TranslationService.translate('accounting.checkout.error.noBillablePositions')
            ];
            return;
        }

        const invalidQuantityPositions = findPositionsWithInvalidQuantity(this.positions);
        if (!this.receiptToCancel?.id && invalidQuantityPositions.length > 0) {
            this.receiptErrorMessages = [buildInvalidQuantityCheckoutError(invalidQuantityPositions)];
            return;
        }
        if (!this.isCancellationMode && this.paymentType === TsePaymentType.CASH && this.givenAmount < Math.abs(this.totalSum)) {
            this.receiptErrorMessages = [
                TranslationService.translate('accounting.checkout.error.givenTooLow')
            ];
            return;
        }

        const paymentType = this.isCancellationMode
            ? mapReceiptPaymentTypeToTse(this.receiptToCancel.paymentType)
            : this.paymentType;

        if (this.isCancellationMode && !paymentType) {
            this.receiptErrorMessages = [
                TranslationService.translate('accounting.checkout.error.cancelPaymentTypeMismatch')
            ];
            return;
        }

        this.receiptErrorMessages = [];
        this.printFailed = false;
        this.requestReceiptDone = false;
        this.canStartPaymentProcess = false;
        this.isProcessing = true;

        let observable: Observable<BcmReceipt>;

        if (this.receiptToCancel?.id) {
            observable = this._cashRegisterService.addCancellationReceipt(
                this.cashRegister,
                this.receiptToCancel,
                paymentType,
                this.address,
                this.person,
                this.company,
                null,
                this.addPositionsBackToCoaster,
                this.cancellationReason,
                this.addressStructured ?? undefined
            );
        } else {
            observable = this._cashRegisterService.addReceipt(
                this.cashRegister,
                this.positions,
                this.paymentType,
                this.address,
                this.person,
                this.company,
                this.wordTemplate,
                this.note,
                undefined,
                undefined,
                this.addressStructured
            );
        }

        observable
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
            next: receipt => {
                this.parentFormGroup?.markAsPristine();
                this.receipt = receipt;
                this.requestReceiptDone = true;
                const paidPositionIds = this.positions.map(paidPosition => paidPosition.id);
                this.updatedPositions = [...(this.person?.positions || this.company?.positions || []).filter(p => !paidPositionIds.includes(p.id))];

                if (this.shouldPrint) {
                    void this._executePrint();
                    return;
                }

                this.isProcessing = false;

                if (this.isCancellationMode) {
                    this._showCancellationSuccessToast();
                    this.dialogRef.close(this.receipt);
                    return;
                }

                this.showReceiptPdf();
            },
            error: err => {
                this.receiptErrorMessages = this._toErrorMessages(err);
                this.receiptRequestFailed = true;
                this.receiptCreationLocked = isTseDocumentCreatedError(err);
                this.printFailed = false;
                this.updatedPositions = this.person?.positions || this.company?.positions || [];
                this.isProcessing = false;
                this.requestReceiptDone = false;
                this.canStartPaymentProcess = true;
            }
            });

    }

    retryPrint(): void {
        if (!this.receipt?.id || this.isPrinting || this.isProcessing) {
            return;
        }
        void this._executePrint();
    }

    finishWithoutPrint(): void {
        if (!this.receipt?.id || this.isPrinting || this.isProcessing) {
            return;
        }
        if (this.isCancellationMode) {
            this._showCancellationSuccessToast();
            this.dialogRef.close(this.receipt);
            return;
        }
        this.dialogRef.close(resolveCheckoutCloseResult(this.receipt, this.updatedPositions));
    }

    cancelCheckout(): void {
        if (this.isProcessing || this.isPrinting || this.receipt?.id) {
            return;
        }
        this.dialogRef.close(null);
    }

    private async _executePrint(): Promise<void> {
        const minimalRecord = this._buildMinimalFinancialRecord();
        this.isPrinting = true;
        this.printFailed = false;

        try {
            const success = await this._printService.printReceiptForRecord(
                minimalRecord,
                this._resolveReceiptPrinter()
            );
            if (!success) {
                this.printFailed = true;
                this.canStartPaymentProcess = true;
                return;
            }
            if (this.isCancellationMode) {
                this._showCancellationSuccessToast();
            }
            this.dialogRef.close(
                resolveCheckoutDialogClosePayload(this.receipt, this.updatedPositions, this.isCancellationMode)
            );
        } finally {
            this.isPrinting = false;
            this.isProcessing = false;
        }
    }

    onReceiptPrinterChange(printer: BcmNetworkPrinter | null): void {
        this.selectedReceiptPrinter = printer;
        this._bcmUserSettingsFacade.updateSingleSetting<BcmUserLastUsedReceiptPrinterSettings>(
            BcmUserSettingKey.LastUsedReceiptPrinter,
            { printerId: printer?.id ?? null },
            true,
        );
    }

    private _loadReceiptPrinters(): void {
        this._networkPrintersService.loadAll()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (printers) => {
                    const currentUserId = this._userService.userId;
                    this.receiptPrinters = this._bcmTenantService.filterUsableReceiptPrinters(printers ?? [], currentUserId);

                    const userSettings = this._bcmUserSettingsState.getCurrentSettings();
                    const lastUsedSettings =
                        userSettings?.[BcmUserSettingKey.LastUsedReceiptPrinter] as BcmUserLastUsedReceiptPrinterSettings | undefined;

                    this.selectedReceiptPrinter = resolveSelectedReceiptPrinter(
                        this.receiptPrinters,
                        lastUsedSettings,
                    );
                    this.receiptPrintersLoaded = true;
                },
                error: () => {
                    this.receiptPrinters = [];
                    this.selectedReceiptPrinter = null;
                    this.receiptPrintersLoaded = true;
                },
            });
    }

    private _resolveReceiptPrinter(): BcmNetworkPrinter | null | undefined {
        if (this.isCancellationMode && this.paymentType === TsePaymentType.CASH) {
            return this.selectedReceiptPrinter;
        }

        return this.data?.receiptPrinter;
    }

    private _showCancellationSuccessToast(): void {
        this._appNotificationService.showSuccess(
            TranslationService.translate('accounting.checkout.status.cancellationSuccess', {
                receiptNumber: this.receipt?.receiptNumber,
                cancelledNumber: this.receiptToCancel?.receiptNumber,
            })
        );
    }

    private _buildMinimalFinancialRecord(): BcmFinancialRecord {
        const printReceipt = this.isCancellationMode
            ? enrichReceiptForCancellationPrint(this.receipt, this.receiptToCancel)
            : this.receipt;

        const record = {
            receipt: printReceipt,
            person: this.person,
            company: this.company,
            address: this.address,
            creatorName: this.receipt.createdBy?.fullName
                || this._cashRegisterService.userFullName
                || '',
            date: this.isCancellationMode
                ? resolveReceiptPrintDate(
                    { date: this.receiptToCancel?.insertedOn ?? this.receipt.insertedOn },
                    printReceipt,
                )
                : (this.receipt.insertedOn ?? new Date()),
            number: this.receipt.receiptNumber,
            type: this.isCancellationMode ? ReceiptTypes.CancellationReceipt : ReceiptTypes.Receipt,
            cancellationNumber: this.isCancellationMode ? this.receiptToCancel?.receiptNumber : undefined,
        } as unknown as BcmFinancialRecord;

        return record;
    }

    private _computeAddressString(): string {
        const structuredAddress = buildStructuredAddressString(this.addressStructured);
        if (structuredAddress) {
            return structuredAddress;
        }

        if (this.person?.id) {
            return this.person.getAddressString('\n', true);
        }

        if (this.company?.id) {
            return this.company.getAddressString('\n', true);
        }

        return '';
    }

    close(): void {
        if (this.isProcessing || this.isPrinting) {
            return;
        }
        this.dialogRef.close(
            resolveCheckoutDialogClosePayload(this.receipt, this.updatedPositions, this.isCancellationMode)
        );
    }

    showReceiptPdf(): void {
        this._router.navigate([this._bcmService.baseUrl, 'accounting', 'invoices', 'view', this.receipt.receiptNumber])
            .then((navigated) => {
                if (!navigated) {
                    this._appNotificationService.showError(
                        TranslationService.translate('accounting.checkout.error.navigateAfterCreateFailed')
                    );
                }
                this.dialogRef.close(
                    resolveCheckoutDialogClosePayload(this.receipt, this.updatedPositions, this.isCancellationMode)
                );
            })
            .catch(() => {
                this._appNotificationService.showError(
                    TranslationService.translate('accounting.checkout.error.navigateAfterCreateFailed')
                );
                this.dialogRef.close(
                    resolveCheckoutDialogClosePayload(this.receipt, this.updatedPositions, this.isCancellationMode)
                );
            });
    }

    @HostListener('window:keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if (this.isCancellationMode) {
            return;
        }
        // Nur verarbeiten wenn kein Textfeld fokussiert ist (außer unserem Dialog)
        const tag = (event.target as HTMLElement)?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') {
            return;
        }
        const key = event.key;
        if (key >= '0' && key <= '9') {
            this.onNumpadKey(key);
        } else if (key === ',' || key === '.') {
            this.onNumpadKey(',');
        } else if (key === 'Backspace') {
            this._numpadBuffer = this._numpadBuffer.length > 1
                ? this._numpadBuffer.slice(0, -1)
                : '0';
            this.givenAmount = parseFloat(this._numpadBuffer.replace(',', '.')) || 0;
        } else if (key === 'Delete' || key === 'Escape') {
            this.onNumpadKey('C');
        } else if (key === 'Enter') {
            event.preventDefault();
        }
    }

    onNumpadKey(key: string): void {
        if (key === 'C') {
            this._numpadBuffer = '0';
        } else if (key === ',') {
            if (!this._numpadBuffer.includes(',')) {
                this._numpadBuffer += ',';
            }
        } else {
            if (this._numpadBuffer === '0') {
                this._numpadBuffer = key;
            } else {
                const parts = this._numpadBuffer.split(',');
                if (parts.length === 2 && parts[1].length >= 2) {
                    return;
                }
                this._numpadBuffer += key;
            }
        }
        this.givenAmount = parseFloat(this._numpadBuffer.replace(',', '.')) || 0;
    }

    setQuickAmount(amount: number): void {
        const newAmount = roundCurrency(this.givenAmount + amount);
        this._numpadBuffer = newAmount.toFixed(2).replace('.', ',');
        this.givenAmount = newAmount;
    }

    setExactAmount(): void {
        const exact = Math.abs(this.totalSum);
        this._numpadBuffer = exact.toFixed(2).replace('.', ',');
        this.givenAmount = exact;
    }

    private _toErrorMessages(err: any): string[] {
        if (Array.isArray(err)) {
            return err.map(item => String(item));
        }
        if (Array.isArray(err?.error)) {
            return err.error.map(item => String(item));
        }
        if (err?.error?.message) {
            return [String(err.error.message)];
        }
        if (err?.message) {
            return [String(err.message)];
        }
        return [TranslationService.translate('common.error.generic')];
    }

}
